/**
 * Land Assessment — eight-lens scoring workspace (Cash Tracker–style page).
 */
import { useMemo, useState } from 'react';
import {
  ArrowCounterClockwise,
  ClockCounterClockwise,
  FloppyDisk,
  Info,
  LinkSimple,
  Paperclip,
  Sparkle,
  Trash,
  Warning,
  X,
} from '@phosphor-icons/react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { API_BASE } from '../config/api';
import { uploadDocument } from '../utils/documentsApi';
import {
  LAND_ASSESSMENT_LENSES,
  SCORE_META,
  buildInterpretationRequest,
  computeAssessmentSummary,
  getAssessmentForScenario,
  hydrateAssessment,
  notesPlaceholder,
  pushHistory,
  radarData,
  resetAssessment,
  restoreFromHistory,
  upsertAssessmentForScenario,
  listFatalFlags,
  setFatalOverride,
  clearFatalOverride,
} from '../utils/landAssessment';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from './ui/hover-card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';

function GuidanceBody({ lens }) {
  return (
    <div className="space-y-2 text-[12px] leading-relaxed text-gray-700">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Core question</div>
        <p className="text-gray-900">{lens.question}</p>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Consider</div>
        <p>{lens.consider}</p>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Red flags</div>
        <p className="text-red-800/90">{lens.redFlags}</p>
      </div>
    </div>
  );
}

function LensGuidance({ lens }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  return (
    <>
      <HoverCard openDelay={120} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className="hidden md:inline-flex w-7 h-7 items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-black/5"
            aria-label={`Guidance for ${lens.name}`}
            data-testid={`land-lens-info-${lens.id}`}
          >
            <Info weight="bold" className="w-3.5 h-3.5" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          className="w-80 p-3 rounded-xl border border-black/10 shadow-lg bg-white z-[80]"
          data-testid={`land-lens-guidance-${lens.id}`}
        >
          <div className="text-[13px] font-semibold text-gray-900 mb-2">{lens.name}</div>
          <GuidanceBody lens={lens} />
        </HoverCardContent>
      </HoverCard>

      <button
        type="button"
        className="md:hidden inline-flex w-8 h-8 items-center justify-center rounded-full text-gray-400 hover:bg-black/5"
        aria-label={`Guidance for ${lens.name}`}
        onClick={() => setSheetOpen(true)}
        data-testid={`land-lens-info-mobile-${lens.id}`}
      >
        <Info weight="bold" className="w-4 h-4" />
      </button>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-left text-[15px]">{lens.name}</SheetTitle>
          </SheetHeader>
          <div className="mt-3 pb-6">
            <GuidanceBody lens={lens} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function ScoreSelector({ lensId, value, onChange }) {
  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 overflow-hidden"
      role="group"
      aria-label="Score"
      data-testid={`land-score-${lensId}`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        const meta = SCORE_META[n];
        return (
          <button
            key={n}
            type="button"
            title={meta.label}
            onClick={() => onChange(active ? null : n)}
            className={`w-9 h-8 text-[12px] font-semibold tabular-nums border-r border-gray-200 last:border-r-0 transition-colors ${
              active ? meta.color : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            data-testid={`land-score-${lensId}-${n}`}
            aria-pressed={active}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function EvidenceList({ lensId, items, dealId, onChange }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const addLink = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    onChange([
      ...items,
      { id: `link-${Date.now()}`, label: trimmed, url: trimmed, documentId: null, fileName: null },
    ]);
    setUrl('');
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !dealId) return;
    setBusy(true);
    try {
      const doc = await uploadDocument(dealId, file);
      if (doc?.id) {
        onChange([
          ...items,
          {
            id: `doc-${doc.id}`,
            label: doc.fileName || file.name,
            url: '',
            documentId: doc.id,
            fileName: doc.fileName || file.name,
          },
        ]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5 space-y-1" data-testid={`land-evidence-${lensId}`}>
      {items.map((ev) => (
        <div key={ev.id} className="flex items-center gap-1.5 text-[11px] text-gray-600">
          {ev.documentId ? <Paperclip className="w-3 h-3 shrink-0" /> : <LinkSimple className="w-3 h-3 shrink-0" />}
          {ev.url ? (
            <a href={ev.url} target="_blank" rel="noreferrer" className="truncate text-blue-600 hover:underline">{ev.label}</a>
          ) : (
            <span className="truncate">{ev.label}</span>
          )}
          <button
            type="button"
            className="ml-auto text-gray-400 hover:text-red-600"
            aria-label="Remove evidence"
            onClick={() => onChange(items.filter((x) => x.id !== ev.id))}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addLink();
            }
          }}
          placeholder="Paste evidence link…"
          className="flex-1 min-w-[140px] h-7 px-2 rounded border border-gray-200 text-[11px]"
          data-testid={`land-evidence-url-${lensId}`}
        />
        <button type="button" onClick={addLink} className="h-7 px-2 rounded border border-gray-200 text-[11px] font-medium hover:bg-gray-50">
          Add link
        </button>
        {dealId ? (
          <label className={`h-7 px-2 rounded border border-gray-200 text-[11px] font-medium hover:bg-gray-50 inline-flex items-center gap-1 cursor-pointer ${busy ? 'opacity-50' : ''}`}>
            <Paperclip className="w-3 h-3" />
            {busy ? 'Uploading…' : 'Attach'}
            <input type="file" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        ) : null}
      </div>
    </div>
  );
}

function LensRow({ lens, assessment, dealId, onPatch }) {
  const score = assessment.scores[lens.id];
  const note = assessment.notes[lens.id] || '';
  const evidence = assessment.evidence[lens.id] || [];

  return (
    <>
      <div
        className="hidden md:grid grid-cols-[minmax(200px,1.2fr)_auto_minmax(220px,1.4fr)] gap-3 items-start px-3 py-3 border-b border-gray-100 last:border-b-0"
        data-testid={`land-lens-row-${lens.id}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-semibold text-gray-900">{lens.name}</span>
            <LensGuidance lens={lens} />
            <span className="text-[10px] tabular-nums text-gray-400 ml-auto">{Math.round(lens.weight * 100)}%</span>
          </div>
          <p className="text-[12px] text-gray-500 mt-0.5">{lens.question}</p>
        </div>
        <div className="pt-0.5">
          <ScoreSelector
            lensId={lens.id}
            value={score}
            onChange={(v) => onPatch({ scores: { ...assessment.scores, [lens.id]: v } })}
          />
        </div>
        <div>
          <textarea
            value={note}
            onChange={(e) => onPatch({ notes: { ...assessment.notes, [lens.id]: e.target.value } })}
            placeholder={notesPlaceholder(score)}
            rows={2}
            className="w-full min-h-[52px] resize-y rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid={`land-notes-${lens.id}`}
          />
          <EvidenceList
            lensId={lens.id}
            items={evidence}
            dealId={dealId}
            onChange={(next) => onPatch({ evidence: { ...assessment.evidence, [lens.id]: next } })}
          />
        </div>
      </div>

      <div
        className="md:hidden border border-gray-200 rounded-xl bg-white p-3 space-y-2"
        data-testid={`land-lens-card-${lens.id}`}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span className="text-[13px] font-semibold text-gray-900">{lens.name}</span>
              <LensGuidance lens={lens} />
            </div>
            <p className="text-[12px] text-gray-500 mt-0.5">{lens.question}</p>
          </div>
          <span className="text-[10px] tabular-nums text-gray-400">{Math.round(lens.weight * 100)}%</span>
        </div>
        <ScoreSelector
          lensId={lens.id}
          value={score}
          onChange={(v) => onPatch({ scores: { ...assessment.scores, [lens.id]: v } })}
        />
        <textarea
          value={note}
          onChange={(e) => onPatch({ notes: { ...assessment.notes, [lens.id]: e.target.value } })}
          placeholder={notesPlaceholder(score)}
          rows={3}
          className="w-full resize-y rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px]"
          data-testid={`land-notes-mobile-${lens.id}`}
        />
        <EvidenceList
          lensId={lens.id}
          items={evidence}
          dealId={dealId}
          onChange={(next) => onPatch({ evidence: { ...assessment.evidence, [lens.id]: next } })}
        />
      </div>
    </>
  );
}

function RadarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-black/10 bg-white px-2.5 py-1.5 text-[11px] shadow-md">
      <div className="font-semibold text-gray-900">{row.fullName}</div>
      <div className="text-gray-600">{row.score != null ? `${row.score} / 5` : 'Not scored'}</div>
    </div>
  );
}

function InterpretationPanel({ interpretation, busy, error, onGenerate, onRefresh }) {
  if (busy) {
    return (
      <div className="border border-gray-200 rounded-xl bg-white p-4 text-[12px] text-gray-500" data-testid="land-ai-busy">
        Generating interpretation…
      </div>
    );
  }
  if (!interpretation) {
    return (
      <div className="border border-dashed border-gray-200 rounded-xl bg-white p-4 space-y-2" data-testid="land-ai-empty">
        <p className="text-[12px] text-gray-500">
          Generate an AI interpretation from the scores and notes. It will not invent facts beyond what you entered.
        </p>
        {error ? <p className="text-[12px] text-red-600">{error}</p> : null}
        <button
          type="button"
          onClick={onGenerate}
          className="h-8 px-3 rounded-lg bg-gray-900 text-white text-[12px] font-semibold inline-flex items-center gap-1.5"
          data-testid="land-ai-generate"
        >
          <Sparkle weight="bold" className="w-3.5 h-3.5" />
          Generate AI Interpretation
        </button>
      </div>
    );
  }

  const decision = interpretation.decision || 'Investigate';
  const decisionCls = {
    Pursue: 'bg-emerald-100 text-emerald-900',
    Investigate: 'bg-amber-100 text-amber-900',
    Hold: 'bg-gray-100 text-gray-800',
    Pass: 'bg-red-100 text-red-900',
  }[decision] || 'bg-gray-100 text-gray-800';

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4 space-y-3" data-testid="land-ai-result">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-[13px] font-semibold text-gray-900">AI interpretation</h3>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${decisionCls}`}>{decision}</span>
        <button
          type="button"
          onClick={onRefresh}
          className="ml-auto h-7 px-2 rounded-lg border border-gray-200 text-[11px] font-medium hover:bg-gray-50"
          data-testid="land-ai-refresh"
        >
          Refresh interpretation
        </button>
      </div>
      {error ? <p className="text-[12px] text-red-600">{error}</p> : null}
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Overall interpretation</h4>
        <p className="text-[12px] text-gray-800 mt-1 whitespace-pre-wrap">{interpretation.overall || '—'}</p>
      </section>
      <section className="grid sm:grid-cols-2 gap-3">
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Principal strengths</h4>
          <ul className="mt-1 space-y-1 text-[12px] text-gray-800 list-disc pl-4">
            {(interpretation.strengths || []).slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Principal risks</h4>
          <ul className="mt-1 space-y-1 text-[12px] text-gray-800 list-disc pl-4">
            {(interpretation.risks || []).slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      </section>
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Missing information</h4>
        <ul className="mt-1 space-y-1 text-[12px] text-gray-800 list-disc pl-4">
          {(interpretation.missingInformation || []).map((s, i) => <li key={i}>{s}</li>)}
          {!(interpretation.missingInformation || []).length ? <li className="list-none text-gray-400">None noted</li> : null}
        </ul>
      </section>
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Recommended next actions</h4>
        <ol className="mt-1 space-y-1 text-[12px] text-gray-800 list-decimal pl-4">
          {(interpretation.nextActions || []).slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </section>
    </div>
  );
}


function FatalFlagsPanel({ assessment, onOverride, onClearOverride }) {
  const flags = listFatalFlags(assessment);
  const [drafts, setDrafts] = useState({});
  if (!flags.length) return null;

  return (
    <div className="mt-3 rounded-xl border border-red-200 bg-red-50/80 p-3 space-y-3" data-testid="land-fatal-flags-panel">
      <div className="text-[13px] font-semibold text-red-900">Fatal land flags</div>
      <p className="text-[12px] text-red-800/90">
        A score of 1 on Planning, Legal, Site or Technical buildability forces a final <strong>NO GO</strong>,
        unless you override with a written justification.
      </p>
      {flags.map((flag) => (
        <div key={flag.lensId} className="rounded-lg border border-red-200 bg-white p-2.5 space-y-2" data-testid={`land-fatal-${flag.lensId}`}>
          <div className="flex items-start gap-1.5 text-[12px] text-red-900">
            <Warning weight="fill" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{flag.name}</div>
              <div className="text-red-800/80">{flag.message}</div>
              {flag.overridden ? (
                <div className="mt-1 text-[11px] text-emerald-800">
                  Overridden: {flag.justification}
                </div>
              ) : null}
            </div>
            {flag.overridden ? (
              <button
                type="button"
                className="text-[11px] font-medium text-red-700 hover:underline"
                onClick={() => onClearOverride(flag.lensId)}
                data-testid={`land-fatal-clear-${flag.lensId}`}
              >
                Clear override
              </button>
            ) : null}
          </div>
          {!flag.overridden ? (
            <div className="space-y-1.5">
              <textarea
                value={drafts[flag.lensId] || ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [flag.lensId]: e.target.value }))}
                rows={2}
                placeholder="Write why this fatal flag can be overridden…"
                className="w-full rounded-lg border border-red-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-red-300"
                data-testid={`land-fatal-justification-${flag.lensId}`}
              />
              <button
                type="button"
                disabled={(drafts[flag.lensId] || '').trim().length < 10}
                onClick={() => {
                  onOverride(flag.lensId, drafts[flag.lensId]);
                  setDrafts((d) => ({ ...d, [flag.lensId]: '' }));
                }}
                className="h-8 px-3 rounded-lg bg-red-900 text-white text-[12px] font-semibold disabled:opacity-40"
                data-testid={`land-fatal-override-${flag.lensId}`}
              >
                Override flag
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function LandAssessmentView({
  landAssessment,
  scenarioId,
  scenarioName,
  dealId,
  dealName,
  address,
  saveStatus = 'idle',
  isDraft = false,
  onSave,
  onChange,
}) {
  const assessment = useMemo(
    () => getAssessmentForScenario(landAssessment, scenarioId),
    [landAssessment, scenarioId],
  );
  const summary = useMemo(() => computeAssessmentSummary(assessment), [assessment]);
  const chartData = useMemo(() => radarData(assessment), [assessment]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  const commit = (nextAssessment) => {
    onChange(upsertAssessmentForScenario(landAssessment, scenarioId, nextAssessment));
  };

  const patch = (partial) => {
    commit(hydrateAssessment({ ...assessment, ...partial }, scenarioId));
  };

  const handleReset = () => {
    if (!window.confirm('Reset this version’s land assessment? Scores, notes and interpretation will be cleared. History is kept.')) {
      return;
    }
    const cleared = resetAssessment(scenarioId);
    cleared.history = assessment.history || [];
    commit(cleared);
  };

  const handleSnapshot = () => {
    commit(pushHistory(assessment));
  };

  const runInterpretation = async () => {
    setAiBusy(true);
    setAiError('');
    try {
      const body = buildInterpretationRequest(assessment, {
        projectName: dealName,
        scenarioName,
        address,
      });
      const res = await fetch(`${API_BASE}/api/land-assessment/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || `Interpretation failed (${res.status})`);
      }
      commit(hydrateAssessment({
        ...assessment,
        interpretation: data,
        updatedAt: new Date().toISOString(),
      }, scenarioId));
    } catch (err) {
      setAiError(err?.message || 'Could not generate interpretation');
    } finally {
      setAiBusy(false);
    }
  };

  const saveLabel = saveStatus === 'saving'
    ? 'Saving…'
    : saveStatus === 'saved'
      ? 'Saved to project'
      : isDraft
        ? 'Not saved to cloud'
        : 'Autosaves with project';

  return (
    <div
      className="w-full max-w-[1100px] mx-auto px-3 sm:px-4 pb-20"
      data-testid="land-assessment-view"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}
    >
      {isDraft ? (
        <div
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-2"
          data-testid="land-draft-save-banner"
        >
          <p className="text-[12px] text-amber-900 flex-1 min-w-[12rem]">
            This is still a draft. Land scores stay on this device until you save the project.
          </p>
          {onSave ? (
            <button
              type="button"
              onClick={onSave}
              className="h-8 px-3 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700 inline-flex items-center gap-1.5 shrink-0"
              data-testid="land-draft-save-btn"
            >
              <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
              Save project…
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start gap-2 py-3 border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] font-semibold tracking-tight text-gray-900">Land Assessment</h1>
          <p className="text-[12px] text-gray-500 truncate" data-testid="land-assessment-project">
            {dealName || 'Untitled project'}
            {scenarioName ? <span className="text-gray-400"> · {scenarioName}</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-gray-500 tabular-nums px-2" data-testid="land-save-status">{saveLabel}</span>
          {onSave ? (
            <button
              type="button"
              onClick={onSave}
              disabled={saveStatus === 'saving'}
              className={`h-8 px-2.5 rounded-lg text-[12px] font-semibold disabled:opacity-60 inline-flex items-center gap-1 ${
                isDraft
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-black/10 text-gray-900 hover:bg-black/15'
              }`}
              data-testid="land-save-btn"
            >
              <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
              {isDraft ? 'Save project…' : 'Save now'}
            </button>
          ) : null}
          <span className="text-[11px] text-gray-500 px-2" data-testid="land-completion">{summary.completionLabel}</span>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="h-8 px-2.5 rounded-lg border border-gray-200 text-[12px] font-medium hover:bg-gray-50 inline-flex items-center gap-1"
            data-testid="land-history-open"
          >
            <ClockCounterClockwise className="w-3.5 h-3.5" />
            Assessment history
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="h-8 px-2.5 rounded-lg border border-gray-200 text-[12px] font-medium hover:bg-gray-50 inline-flex items-center gap-1"
            data-testid="land-reset"
          >
            <ArrowCounterClockwise className="w-3.5 h-3.5" />
            Reset assessment
          </button>
          <button
            type="button"
            onClick={runInterpretation}
            disabled={aiBusy || summary.scoredCount === 0}
            className="h-8 px-3 rounded-lg bg-gray-900 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="land-ai-generate-header"
          >
            <Sparkle weight="bold" className="w-3.5 h-3.5" />
            Generate AI Interpretation
          </button>
        </div>
      </div>

      <section className="mt-3 border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid="land-lenses">
        <div className="hidden md:block">
          {LAND_ASSESSMENT_LENSES.map((lens) => (
            <LensRow key={lens.id} lens={lens} assessment={assessment} dealId={dealId} onPatch={patch} />
          ))}
        </div>
        <div className="md:hidden p-2 space-y-2">
          {LAND_ASSESSMENT_LENSES.map((lens) => (
            <LensRow key={lens.id} lens={lens} assessment={assessment} dealId={dealId} onPatch={patch} />
          ))}
        </div>
      </section>

      <FatalFlagsPanel
        assessment={assessment}
        onOverride={(lensId, justification) => commit(setFatalOverride(assessment, lensId, justification))}
        onClearOverride={(lensId) => commit(clearFatalOverride(assessment, lensId))}
      />

      <section className="mt-4 grid lg:grid-cols-[1.1fr_0.9fr] gap-3" data-testid="land-results">
        <div className="border border-gray-200 rounded-xl bg-white p-4">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-full sm:w-[280px] h-[260px]" data-testid="land-radar">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#6b7280', fontSize: 10 }} />
                  <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fill: '#9ca3af', fontSize: 9 }} />
                  <Radar name="Score" dataKey="value" stroke="#111827" fill="#111827" fillOpacity={0.15} isAnimationActive={false} />
                  <Tooltip content={<RadarTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="min-w-[160px] flex-1 space-y-2" data-testid="land-score-summary">
              <div>
                <div className="text-[28px] font-semibold tabular-nums tracking-tight text-gray-900" data-testid="land-overall-score">
                  {summary.overallScore != null ? `${summary.overallScore} / 100` : '— / 100'}
                </div>
                <p className="text-[13px] text-gray-600" data-testid="land-verdict">{summary.verdictLabel}</p>
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
                <div>
                  <dt className="text-gray-400">Land Potential</dt>
                  <dd className="font-semibold tabular-nums">{summary.landPotential != null ? summary.landPotential : '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-400">Deal Potential</dt>
                  <dd className="font-semibold tabular-nums">{summary.dealPotential != null ? summary.dealPotential : '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-400">Confidence</dt>
                  <dd className="font-semibold tabular-nums">{summary.confidence}%</dd>
                </div>
                <div>
                  <dt className="text-gray-400">Unscored</dt>
                  <dd className="font-semibold tabular-nums">{summary.unscoredCount}</dd>
                </div>
              </dl>
              {summary.criticalFlags.length > 0 ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 space-y-1" data-testid="land-critical-flags">
                  {summary.criticalFlags.map((f) => (
                    <div key={f.lensId} className="flex items-start gap-1.5 text-[12px] text-red-900">
                      <Warning weight="fill" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span><strong>Critical Flag:</strong> {f.message}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <InterpretationPanel
          interpretation={assessment.interpretation}
          busy={aiBusy}
          error={aiError}
          onGenerate={runInterpretation}
          onRefresh={runInterpretation}
        />
      </section>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Assessment history</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2" data-testid="land-history-list">
            <button
              type="button"
              onClick={handleSnapshot}
              className="w-full h-9 rounded-lg bg-gray-900 text-white text-[12px] font-semibold"
              data-testid="land-history-snapshot"
            >
              Save snapshot now
            </button>
            {(assessment.history || []).length === 0 ? (
              <p className="text-[12px] text-gray-500">No snapshots yet.</p>
            ) : (
              (assessment.history || []).map((h) => (
                <div key={h.id} className="border border-gray-200 rounded-lg p-2.5 flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold tabular-nums">
                      {h.overallScore != null ? `${h.overallScore}/100` : '—'}
                      <span className="font-normal text-gray-500"> · {h.verdictLabel}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">{h.savedAt ? new Date(h.savedAt).toLocaleString() : ''}</div>
                  </div>
                  <button
                    type="button"
                    className="text-[11px] font-medium text-blue-600"
                    onClick={() => {
                      commit(restoreFromHistory(assessment, h.id));
                      setHistoryOpen(false);
                    }}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Delete snapshot"
                    onClick={() => commit({
                      ...assessment,
                      history: (assessment.history || []).filter((x) => x.id !== h.id),
                    })}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
